import { Title } from "ui/Typography"
import BookingTable from "./BookingTable"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState, useCallback, useMemo } from "react"
import { getClientBookingsForGuides } from "store/bookings"
import { Flex, DatePicker, Card, Button, Select } from "antd"
import { useTranslation } from "react-i18next"
import SearchInput from "ui/SearchInput"
import WithEmptyState from "ui/WithEmptyStateWrapper"
import { debounce } from "lodash"
import PropTypes from "prop-types"
import styles from "../index.module.css"
import moment from "moment"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import { routes } from "router"
import { useNavigate } from "react-router-dom"
import { BookingNotFound } from "ui/Icon/DSIcon/BookingNotFound"
const { RangePicker } = DatePicker
const dateFormat = "MM/DD/YYYY"

const BookingTab = ({ active, id }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState("")
  const [sortOrder, setSortOrder] = useState("")
  const [dateRange, setDateRange] = useState(null)
  const { items, metadata } = useSelector((store) => store.bookings.clientBookingForGuides)
  const { adventure_mode } = useSelector((store) => store.user)
  const client = useSelector((state) => state.clients.client)
  const [isAppend, setIsAppend] = useState(false)
  const navigate = useNavigate()

  useLoaderWatchers({ watchers: ["bookings.clientBookingForGuides"] })

  // Create a stable reference to the debounced function
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value)
        setPage(1) // Reset to first page when search changes
      }, 300),
    []
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel()
    }
  }, [debouncedSetSearchQuery])

  // Handle search input changes
  const handleSearchChange = useCallback(
    (value) => {
      setSearchTerm(value)
      debouncedSetSearchQuery(value)
    },
    [debouncedSetSearchQuery]
  )

  useEffect(() => {
    if (active && id) {
      // Build the query parameters object
      const queryParams = new URLSearchParams()

      // Add base parameters
      queryParams.append("page", page)
      queryParams.append("per_page", pageSize)

      // Add search parameter if exists
      if (searchQuery) {
        queryParams.append("search", searchQuery)
      }

      // Add sort parameters if they exist
      if (sortKey) {
        queryParams.append("sort", sortKey)
        queryParams.append("direction", sortOrder)
      }

      if (dateRange?.startDate && dateRange?.endDate) {
        const { startDate, endDate } = dateRange
        queryParams.append("created_from", moment(startDate).format("YYYY-MM-DD"))
        queryParams.append("created_to", moment(endDate).format("YYYY-MM-DD"))
      }
      // Convert URLSearchParams to object for the API call
      dispatch(getClientBookingsForGuides(id, isAppend, Object.fromEntries(queryParams)))
    }
  }, [active, id, page, pageSize, sortKey, sortOrder, searchQuery, dispatch, dateRange, isAppend])

  const onSortChange = (pagination, filters, sorter) => {
    setSortKey(sorter?.field)
    setSortOrder(sorter?.order === "ascend" ? "asc" : "desc")
    setPage(1) // Reset to first page when sorting changes
  }

  const handleSortChange = (value) => {
    const [key, order] = value.split("#")
    setSortKey(key)
    setSortOrder(order)
    setPage(1)
  }

  const handleDateChange = (_, dateStrings) => {
    if (dateStrings && dateStrings.length === 2 && dateStrings[0] && dateStrings[1]) {
      setDateRange({
        startDate: dateStrings[0],
        endDate: dateStrings[1]
      })
    } else {
      setDateRange(null)
    }
    setPage(1)
  }

  return (
    <FirstLoading name="bookings.clientBookingForGuides">
      <Card>
        <Flex vertical gap={16}>
          <Title level={5}>{t("client.bookings")}</Title>
          <Flex gap={16} className="w-full" wrap="wrap">
            <SearchInput
              className={styles.clientSearchInput}
              searchBy={searchTerm}
              setSearchBy={handleSearchChange}
              ariaLabel={t("clients.aria.search", { name: "bookings" })}
              ariaControls={t("clients.aria.search_result", { name: "bookings" })}
              placeholder={t("global.search")}
            />
            <RangePicker
              className={styles.clientRangePicker}
              format={dateFormat}
              onChange={handleDateChange}
              placeholder={["From", "To"]}
            />
            <Select className={styles.clientSortByFilter} placeholder="Sort By" onChange={handleSortChange}>
              <Select.Option value="date#asc">Date (A to Z)</Select.Option>
              <Select.Option value="created_at#desc">Date (Z to A)</Select.Option>
              <Select.Option value="subject#asc">Experience (A to Z)</Select.Option>
              <Select.Option value="subject#desc">Experience (Z to A)</Select.Option>
              <Select.Option value="subject#asc">Amount Spent (A to Z)</Select.Option>
              <Select.Option value="subject#desc">Amount Spent (Z to A)</Select.Option>
              <Select.Option value="subject#asc">Status (A to Z)</Select.Option>
              <Select.Option value="subject#desc">Status (Z to A)</Select.Option>
            </Select>
            <Button
              type="primary"
              className={`${styles.addBookingBtn} px-xl py-l ml-auto`}
              onClick={() => navigate(routes.guideBookingNewPath(), { state: { client } })}
            >
              {t("client.add_booking")}
            </Button>
          </Flex>
          <WithEmptyState
            name="Booking"
            hasItems={items?.length > 0}
            fallbackTitle="No bookings yet."
            fallbackImageSrc={<BookingNotFound adventureMode={adventure_mode}/>}
            className="overflow-auto"
          >
            <BookingTable
              page={page}
              pageSize={pageSize}
              sortKey={sortKey}
              sortOrder={sortOrder}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onSortChange={onSortChange}
              items={items}
              metadata={metadata}
              setIsAppend={setIsAppend}
            />
          </WithEmptyState>
        </Flex>
      </Card>
    </FirstLoading>
  )
}

BookingTab.propTypes = {
  active: PropTypes.bool,
  id: PropTypes.string
}

export default BookingTab
